import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { applyAutofill, AUTOFILL_FIELDS, clearSuggestions, LOOKUP_DEBOUNCE_MS, type AutofillField } from "@server/lookups/autofill";
import { parseCompanyUrl } from "@server/lookups/company-url";
import { checkFormValues } from "@server/lists/check";
import { activeLabels, type StoredFormLists } from "@server/lists/types";
import { HUDDLE_ANSWERS, SEVERITIES, engFormSchema } from "@server/submissions/schema";
import { MAX_FILES, type FileRef } from "@server/uploads/files";
import { ApiError, lookupCompany, useCreateSubmission, useFormLists } from "../api/client";
import { FilePicker, fileRefs, unfinishedFiles, type Attached } from "../components/FilePicker";
import { Button, ErrorBanner, Field, Select } from "../components/ui";
import { Workspace } from "../components/Workspace";
import { loadDraft, saveDraft } from "../draft";

interface Values {
  huddle: string;
  huddleConfirmed: boolean;
  appUrl: string;
  companyName: string;
  businessUnit: string;
  pricingPlan: string;
  feature: string;
  severity: string;
  details: string;
}

const EMPTY: Values = {
  huddle: "",
  huddleConfirmed: false,
  appUrl: "",
  companyName: "",
  businessUnit: "",
  pricingPlan: "",
  feature: "",
  severity: "",
  details: "",
};

type Errors = Partial<Record<keyof Values, string>>;

interface Draft {
  values: Values;
  files: Attached[];
  touched: AutofillField[];
  suggested: AutofillField[];
}

// Whatever is in storage is only trusted field by field, so an old or edited draft can't break the form.
function restoreDraft(): Draft {
  const stored = loadDraft();
  const draft = typeof stored === "object" && stored !== null ? (stored as Partial<Record<keyof Draft, unknown>>) : {};
  const saved = typeof draft.values === "object" && draft.values !== null ? (draft.values as Record<string, unknown>) : {};
  const values: Values = { ...EMPTY };
  for (const key of Object.keys(EMPTY) as (keyof Values)[]) {
    if (typeof saved[key] === typeof EMPTY[key]) {
      Object.assign(values, { [key]: saved[key] });
    }
  }
  const fields = (list: unknown): AutofillField[] =>
    Array.isArray(list) ? AUTOFILL_FIELDS.filter((field) => list.includes(field)) : [];
  const files = Array.isArray(draft.files)
    ? (draft.files as Attached[]).filter((file) => file?.status === "ready" && file.ref && typeof file.name === "string")
    : [];
  return { values, files, touched: fields(draft.touched), suggested: fields(draft.suggested) };
}

function validate(values: Values, files: FileRef[], lists: StoredFormLists): { errors: Errors; form?: ReturnType<typeof engFormSchema.parse> } {
  const parsed = engFormSchema.safeParse({
    ...values,
    appUrl: values.appUrl.trim() || undefined,
    feature: values.feature || undefined,
    files: files.length > 0 ? files : undefined,
  });
  const errors: Errors = {};
  if (parsed.success) {
    // A value an admin has since retired, from an old draft or a suggestion.
    for (const issue of checkFormValues(parsed.data, lists)) {
      errors[issue.path as keyof Values] = issue.message;
    }
    return Object.keys(errors).length === 0 ? { errors, form: parsed.data } : { errors };
  }
  for (const issue of parsed.error.issues) {
    const key = issue.path[0] as keyof Values | undefined;
    if (!key || errors[key]) {
      continue;
    }
    const empty = values[key] === "";
    errors[key] = empty ? "Required" : issue.message;
  }
  return { errors };
}

export function SubmissionForm() {
  const navigate = useNavigate();
  const create = useCreateSubmission();
  const [initial] = useState(restoreDraft);
  const [values, setValues] = useState<Values>(initial.values);
  const [errors, setErrors] = useState<Errors>({});
  const [attached, setAttached] = useState<Attached[]>(initial.files);
  const [filesError, setFilesError] = useState<string>();
  const lists = useFormLists();
  const offered = {
    businessUnits: activeLabels(lists.data?.businessUnits ?? []),
    pricingPlans: activeLabels(lists.data?.pricingPlans ?? []),
    features: activeLabels(lists.data?.features ?? []),
  };

  // A draft, or a suggestion, can hold a value an admin has retired since: it goes, so the person picks again.
  useEffect(() => {
    const stored = lists.data;
    if (!stored) {
      return;
    }
    const keep = (value: string, entries: { label: string; retired?: boolean }[]): string => (value === "" || activeLabels(entries).includes(value) ? value : "");
    setValues((current) => ({
      ...current,
      businessUnit: keep(current.businessUnit, stored.businessUnits),
      pricingPlan: keep(current.pricingPlan, stored.pricingPlans),
      feature: keep(current.feature, stored.features),
    }));
  }, [lists.data]);

  const [lookupState, setLookupState] = useState<"idle" | "looking" | "suggested">(initial.suggested.length > 0 ? "suggested" : "idle");
  // Fields the person has edited themselves, and fields a suggestion filled. Refs, because the lookup runs outside rendering.
  const touched = useRef(new Set<AutofillField>(initial.touched));
  const suggested = useRef(new Set<AutofillField>(initial.suggested));
  // A restored draft already has its suggestion, so its URL isn't looked up again until the person changes it.
  const restoredUrl = useRef<string | undefined>(initial.values.appUrl.trim() || undefined);

  useEffect(() => {
    const files = attached.filter((file) => file.status === "ready");
    saveDraft({ values, files, touched: [...touched.current], suggested: [...suggested.current] } satisfies Draft);
  }, [values, attached]);

  const set = <K extends keyof Values>(key: K, value: Values[K]): void => {
    if ((AUTOFILL_FIELDS as readonly string[]).includes(key)) {
      touched.current.add(key as AutofillField);
    }
    setValues((current) => ({ ...current, [key]: value }));
  };

  // A company URL fills in what Vestd knows about the company. It never blocks the form, and any failure is silent.
  useEffect(() => {
    if (restoredUrl.current !== undefined && values.appUrl.trim() === restoredUrl.current) {
      return undefined;
    }
    restoredUrl.current = undefined;
    // Whatever an earlier URL suggested is not this URL's, so it goes unless the person has edited it.
    const dropSuggestions = (): void => {
      const stale = clearSuggestions(touched.current, suggested.current);
      if (Object.keys(stale.values).length > 0) {
        setValues((current) => ({ ...current, ...stale.values }));
      }
      suggested.current = new Set();
    };
    dropSuggestions();
    setLookupState("idle");

    const appUrl = values.appUrl.trim();
    if (!parseCompanyUrl(appUrl)) {
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLookupState("looking");
      lookupCompany(appUrl, controller.signal).then(
        (suggestion) => {
          if (controller.signal.aborted) {
            return;
          }
          const patch = suggestion ? applyAutofill(suggestion, touched.current, suggested.current) : undefined;
          if (patch) {
            setValues((current) => ({ ...current, ...patch.values }));
            suggested.current = new Set(patch.filled);
            // These fields now hold the suggestion; only an edit from here on counts as the person's own.
            for (const field of patch.filled) {
              touched.current.delete(field);
            }
          } else {
            dropSuggestions();
          }
          setLookupState(patch && patch.filled.length > 0 ? "suggested" : "idle");
        },
        () => {
          if (!controller.signal.aborted) {
            dropSuggestions();
            setLookupState("idle");
          }
        },
      );
    }, LOOKUP_DEBOUNCE_MS);
    // A changed URL ignores whatever the earlier lookup finds.
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [values.appUrl]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!lists.data) {
      return;
    }
    const { errors: found, form } = validate(values, fileRefs(attached), lists.data);
    setErrors(found);
    const unfinished = unfinishedFiles(attached);
    setFilesError(unfinished);
    if (!form || unfinished) {
      return;
    }
    create.mutate(form, { onSuccess: (view) => void navigate(`/submissions/${view.id}`) });
  };

  // The three fields the lookup fills in are locked while it runs, so it can't overwrite what the person is typing.
  const looking = lookupState === "looking";
  const sendError = create.error instanceof ApiError ? create.error.message : create.error ? "Something went wrong. Please try again." : undefined;

  return (
    <Workspace step="form">
      <div className="phase-head">
        <h1>Raise an engineering ticket</h1>
        <p>
          Please answer the questions below. It's triaged immediately — a platform issue becomes an engineering ticket
          automatically; anything else is explained to you here instead. Live incidents are not raised here — use the incident form.
        </p>
      </div>

      <form className="issue-form" onSubmit={submit} noValidate>
        <div className="field">
          <Field
            id="huddle"
            label="Has the issue been raised in #cs-question-huddle or talked through with other team members?"
            error={errors.huddle}
            hint="Has the issue been discussed in #cs-question-huddle? If it's a critical issue and you know a huddle is not necessary, choose N/A."
          >
            <Select id="huddle" required value={values.huddle} onChange={(e) => set("huddle", e.target.value)} options={HUDDLE_ANSWERS} />
          </Field>
          {values.huddle === "No" && (
            <div className={`nudge${errors.huddleConfirmed ? " has-error" : ""}`}>
              <p>
                It's usually best practice to raise issues in a huddle first. If you're confident this needs an engineering ticket right now,
                you can carry on.
              </p>
              <label className="nudge-confirm">
                <input type="checkbox" checked={values.huddleConfirmed} onChange={(e) => set("huddleConfirmed", e.target.checked)} />
                I'm confident this needs to go straight to engineering
              </label>
              {errors.huddleConfirmed && (
                <span className="nudge-error">Please confirm before continuing, or choose N/A if a huddle isn't needed.</span>
              )}
            </div>
          )}
        </div>

        <Field
          id="appUrl"
          label="App URL"
          optional
          error={errors.appUrl}
          hint="If you have the URL for the affected company and page, please add it here. If it affects multiple companies, leave this blank."
        >
          <input
            id="appUrl"
            type="text"
            autoComplete="off"
            value={values.appUrl}
            onChange={(e) => set("appUrl", e.target.value)}
            placeholder="e.g. https://app.vestd.com/company/8982/employee/5944"
          />
          {lookupState === "looking" && (
            <p className="hint" role="status">
              Looking up company…
            </p>
          )}
          {lookupState === "suggested" && (
            <p className="hint" role="status">
              Suggested from Vestd – check before submitting
            </p>
          )}
        </Field>

        <div className="field-row">
          <Field id="companyName" label="Company name" error={errors.companyName} hint="Affects more than one company, or it's on demo? Put N/A.">
            <input
              id="companyName"
              type="text"
              maxLength={200}
              value={values.companyName}
              disabled={looking}
              onChange={(e) => set("companyName", e.target.value)}
              placeholder="e.g. Odin Space Group RUV"
            />
          </Field>
          <Field
            id="businessUnit"
            label="Business unit"
            error={errors.businessUnit}
            hint="Affecting all companies? Choose All BU's."
          >
            <Select id="businessUnit" required disabled={looking} value={values.businessUnit} onChange={(e) => set("businessUnit", e.target.value)} options={offered.businessUnits} />
          </Field>
        </div>

        <div className="field-row">
          <Field
            id="pricingPlan"
            label="Pricing plan"
            error={errors.pricingPlan}
            hint="Affecting all pricing plans? Choose All Plans."
          >
            <Select id="pricingPlan" required disabled={looking} value={values.pricingPlan} onChange={(e) => set("pricingPlan", e.target.value)} options={offered.pricingPlans} />
          </Field>
          <Field
            id="feature"
            label="Feature"
            optional
            error={errors.feature}
            hint="Not listed? Mention it in the details below — we'll notify the PMs and add it."
          >
            <Select id="feature" placeholder="—" value={values.feature} onChange={(e) => set("feature", e.target.value)} options={offered.features} />
          </Field>
        </div>

        <Field id="severity" label="Severity" error={errors.severity}>
          <Select id="severity" required value={values.severity} onChange={(e) => set("severity", e.target.value)} options={SEVERITIES} />
        </Field>

        <Field id="details" label="Details" error={errors.details} hint="Describe the issue in as much detail as possible.">
          <textarea
            id="details"
            maxLength={10_000}
            value={values.details}
            onChange={(e) => set("details", e.target.value)}
            placeholder="What did you expect, what actually happened, and how to reproduce it"
          />
        </Field>

        <FilePicker
          attached={attached}
          onChange={(update) => setAttached(update)}
          error={filesError}
          hint={`Up to ${MAX_FILES} files, 10 MB each: images, PDF, CSV, XLSX, DOCX or text. They are attached to the Jira ticket.`}
        />

        {lists.isError && <ErrorBanner>We couldn't load the lists for this form. Reload the page to try again.</ErrorBanner>}
        {sendError && <ErrorBanner>{sendError}</ErrorBanner>}
        <div className="form-actions">
          <Button type="submit" disabled={!lists.data || create.isPending || attached.some((item) => item.status === "uploading")}>
            {create.isPending ? "Sending…" : "Run AI triage →"}
          </Button>
        </div>
      </form>
    </Workspace>
  );
}
